import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Chrome } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const user = await response.json();
      toast.success('Login successful!');
      
      if (user.is_admin) {
        navigate('/admin', { state: { user } });
      } else if (user.role === 'customer') {
        navigate('/customer', { state: { user } });
      } else {
        navigate('/runner', { state: { user } });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/customer';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center px-4">
      <Card data-testid="login-card" className="w-full max-w-md bg-white border border-slate-100 shadow-lg rounded-xl p-8">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Errands Boy" className="h-24 w-24 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome Back</h1>
          <p className="text-slate-600">Login to your Errands Boy account</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg input-focus"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg input-focus"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-3 font-medium shadow-lg hover:shadow-teal-700/25 btn-hover"
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>

          <button
            data-testid="forgot-password-link"
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="w-full text-sm text-slate-600 hover:text-slate-900 text-center"
          >
            Forgot password?
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-slate-500">Or continue with</span>
          </div>
        </div>

        <Button
          data-testid="google-login-btn"
          onClick={handleGoogleLogin}
          className="w-full bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-full py-3 font-medium btn-hover flex items-center justify-center gap-2"
        >
          <Chrome className="w-5 h-5" />
          Continue with Google
        </Button>

        <p className="text-center text-sm text-slate-600 mt-6">
          Don't have an account?{' '}
          <button
            data-testid="register-link"
            onClick={() => navigate('/register')}
            className="text-teal-700 font-medium hover:underline"
          >
            Sign up
          </button>
        </p>
      </Card>
    </div>
  );
}